import { useRef, useState } from "react";
import { Alert, Button, Col, FormGroup, FormLabel, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import InputDate from "../../../components/Forms/Element/InputDate";
import InputText from "../../../components/Forms/Element/InputText";
import TextArea from "../../../components/Forms/Element/TextArea";
import CustomSelect from "../../../components/Forms/Element/CustomSelect";
import SegmentedButtons from "../../../components/Forms/Element/SegmentedButtons";
import ColorField from "../../../components/Forms/Element/ColorField";
import AddressRegistration from "../../../components/Custom/Common/Address/AddressRegistration";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import FormSection from "../../../components/Common/Form/FormSection";
import HintTooltip from "../../../components/Common/HintTooltip";
import CopyButton from "../../../components/Common/CopyButton";
import { ApiError } from "../../../components/Common/ApiError";
import { warnAlert, warnAlertYesOrNo } from "../../../components/alert/defaultAlert";
import Api from "../../../../constants/Api";
import axiosInstance from "../../../../services/AxiosInstance";
import { clubImageUrl, whatsappUrl } from "../../../../utils/ClubUtils";
import "./ClubPartner.css";

// Seis regras de até 165 caracteres, com as quebras de linha, cabem no limite de 1.000.
const MAX_RULES = 6;
const MAX_RULE_LENGTH = 165;
const MAX_LOGO_SIZE = 2 * 1024 * 1024;
const MAX_COVER_SIZE = 5 * 1024 * 1024;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const HINTS = {
    category: 'Agrupa os parceiros em abas na vitrine do cliente ("Gastronomia", "Esportes"). Escolha uma já usada ou digite uma nova.',
    featured: 'Parceiro em destaque ganha o selo e a capa dele entra no carrossel do topo da vitrine.',
    brandColor: 'Cor do parceiro. Pinta o card dele na vitrine quando ainda não há capa, e a inicial do nome quando não há logo.',
    logo: 'Imagem quadrada, de preferência com fundo transparente ou branco. PNG, JPG ou WEBP, até 2MB.',
    cover: 'Imagem larga (proporção 16 por 7), como um banner. É a arte que mais aparece na vitrine. PNG, JPG ou WEBP, até 5MB.',
    plans: 'Planos que têm direito à oferta. Deixe vazio para valer para qualquer assinante.',
    usageLimit: 'Quantos cupons cada cliente pode gerar. Deixe vazio para não limitar.',
    rules: 'Condições da oferta, uma por item (ex.: "Válido de segunda a quinta", "Não cumulativo"). O cliente as vê como lista, o que é mais fácil de ler do que um texto corrido.',
    validity: 'Quantos dias o cupom vale depois que o cliente o gera. Curto, para não virar cupom parado.'
};

/** Oferta em branco, com a validade do cupom que a maioria dos parceiros usa. */
export const emptyOffer = () => ({
    id: null,
    title: '',
    // Regras como lista, uma por linha no servidor: cada regra vira um item na vitrine.
    rulesList: [],
    validFrom: null,
    validUntil: null,
    plans: [],
    usageLimit: '',
    usageLimitPeriod: 'MONTH',
    couponValidityDays: 7,
    status: 'ACTIVE',
    couponsGenerated: 0
});

/** Escolha de uma imagem (logo ou capa) com prévia, antes mesmo de o parceiro estar salvo. */
const ArtSlot = ({ title, hint, kind, savedPath, file, removed, maxSize, onPick, onRemove }) => {
    const inputRef = useRef(null);
    const [preview, setPreview] = useState(null);

    const handleChange = (event) => {
        const picked = event.target.files?.[0];
        event.target.value = '';
        if (!picked) return;
        if (!IMAGE_TYPES.includes(picked.type)) {
            warnAlert('A imagem deve ser PNG, JPG ou WEBP.');
            return;
        }
        if (picked.size > maxSize) {
            warnAlert(`A imagem deve ter no máximo ${maxSize / (1024 * 1024)}MB.`);
            return;
        }
        setPreview(URL.createObjectURL(picked));
        onPick(picked);
    };

    const shown = preview || (!removed ? clubImageUrl(savedPath) : null);

    return (
        <div className="club-art__slot">
            <FormLabel>{title} <HintTooltip text={hint} /></FormLabel>
            <div className={`club-art__preview club-art__preview--${kind}`}>
                {shown ? <img src={shown} alt={title} /> : <span>Nenhuma imagem</span>}
            </div>
            <div className="club-art__actions">
                <Button size="sm" variant="outline-primary" onClick={() => inputRef.current?.click()}>
                    <i className="fa-solid fa-image me-1"></i>{shown ? 'Trocar' : 'Escolher imagem'}
                </Button>
                {shown && (
                    <Button size="sm" variant="outline-danger" onClick={() => { setPreview(null); onRemove(); }}>
                        Remover
                    </Button>
                )}
            </div>
            <input ref={inputRef} type="file" accept={IMAGE_TYPES.join(',')} hidden onChange={handleChange} />
        </div>
    );
};

/** Acesso é consultado sob demanda; PINs antigos precisam ser renovados. */
const AccessSection = ({ entityData, company, onGenerated }) => {
    const [access, setAccess] = useState(null);
    const [generating, setGenerating] = useState(false);

    const generate = () => {
        setGenerating(true);
        axiosInstance.post(`${Api.subscription.club_partner}/${entityData.id}/access`)
            .then(({ data }) => {
                setAccess({ url: `${window.location.origin}${data.accessPath}`, pin: data.pin });
                onGenerated?.();
            })
            .catch(ApiError)
            .finally(() => setGenerating(false));
    };

    const handleGenerate = () => {
        if (!entityData.accessConfigured) {
            generate();
            return;
        }
        warnAlertYesOrNo('Gerar um novo acesso troca o link e o PIN: os atuais e as sessões abertas no celular do parceiro deixam de funcionar. Continuar?', generate);
    };

    const message = access
        ? `Olá! Para validar os cupons do Clube ${company?.name || ''}, abra ${access.url} e entre com o PIN ${access.pin}.`
        : '';

    return (
        <FormSection
            title="Acesso do parceiro"
            description="O parceiro valida os cupons num link próprio, protegido por um PIN. Não precisa de cadastro nem de aplicativo."
            icon="fa-solid fa-key"
        >
            {!entityData ? (
                <Alert variant="info" className="py-2 mb-0">
                    <i className="fa-solid fa-circle-info me-2"></i>Salve o parceiro para gerar o link e o PIN de validação.
                </Alert>
            ) : (
                <>
                    <p className="mb-3">
                        {entityData.accessConfigured
                            ? 'O acesso está liberado. Consulte o link e o PIN ou gere um novo acesso.'
                            : 'O parceiro ainda não tem acesso. Gere o link e o PIN e envie a ele.'}
                    </p>
                    {entityData.accessConfigured && <Button variant="outline-primary" size="sm" className="me-2" disabled={generating} onClick={() => {
                        setGenerating(true);
                        axiosInstance.get(`${Api.subscription.club_partner}/${entityData.id}/access`)
                            .then(({ data }) => data.pin
                                ? setAccess({ url: `${window.location.origin}${data.accessPath}`, pin: data.pin })
                                : warnAlertYesOrNo('Este PIN antigo não pode ser recuperado. Gerar um novo acesso troca o link e encerra as sessões atuais. Continuar?', generate))
                            .catch(ApiError)
                            .finally(() => setGenerating(false));
                    }}><i className="fa-solid fa-eye me-1"></i>Ver acesso</Button>}
                    <Button variant="primary" size="sm" onClick={handleGenerate} disabled={generating}>
                        <i className="fa-solid fa-key me-1"></i>{entityData.accessConfigured ? 'Gerar novo acesso' : 'Gerar acesso'}
                    </Button>
                </>
            )}

            <Modal show={Boolean(access)} onHide={() => setAccess(null)} centered>
                <Modal.Header closeButton><Modal.Title>Acesso do parceiro</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p className="mb-1">Link de validação</p>
                    <div className="d-flex gap-2 align-items-center mb-3">
                        <strong style={{ overflowWrap: 'anywhere', flex: 1 }}>{access?.url}</strong>
                        <CopyButton textToCopy={access?.url} showText={false} />
                    </div>
                    <p className="mb-1">PIN</p>
                    <div className="club-access__pin">{access?.pin}</div>
                    <Alert variant="warning" className="py-2 mt-3 mb-0">
                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                        Este PIN pode ser consultado novamente pelos gestores com acesso ao Clube.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <CopyButton textToCopy={message} label="Copiar mensagem" />
                    <Button as="a" variant="success" size="sm" target="_blank" rel="noreferrer"
                        href={whatsappUrl(entityData?.whatsapp, message) || `https://wa.me/?text=${encodeURIComponent(message)}`}>
                        <i className="fa-brands fa-whatsapp me-1"></i>Enviar por WhatsApp
                    </Button>
                </Modal.Footer>
            </Modal>
        </FormSection>
    );
};

const Form = ({ formik, entityData, planOptions, categories, company, onAccessGenerated }) => {
    const { t } = useTranslation();
    const offers = formik.values.offers;

    const updateOffer = (index, patch) => formik.setFieldValue('offers', offers.map((offer, i) => (i === index ? { ...offer, ...patch } : offer)));
    const addOffer = () => formik.setFieldValue('offers', [...offers, emptyOffer()]);
    // Oferta já salva é retirada da vitrine (os cupons emitidos continuam contando); a nova some.
    // Regras da oferta: lista de itens curtos, no máximo seis, para a vitrine não virar um texto longo.
    const setRules = (index, rulesList) => updateOffer(index, { rulesList });
    const removeOffer = (index) => {
        const offer = offers[index];
        if (offer.id) {
            updateOffer(index, { status: 'INACTIVE' });
        } else {
            formik.setFieldValue('offers', offers.filter((_, i) => i !== index));
        }
    };

    const offerError = (index, field) => formik.touched.offers?.[index]?.[field] && formik.errors.offers?.[index]?.[field];

    return (
        <div className="form-layout form-layout--spaced club-form">
            <FormSection title="Dados do parceiro" description="Como o parceiro aparece na vitrine do Clube." icon="fa-solid fa-handshake">
                <Row className="g-3">
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>{t('name')} <AsteriskRequired /></FormLabel>
                            <InputText name="name" maxLength={120} value={formik.values.name}
                                onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.name && formik.errors.name} />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>{t('category')} <HintTooltip text={HINTS.category} /></FormLabel>
                            <InputText name="category" list="club-categories" maxLength={60} value={formik.values.category}
                                onChange={formik.handleChange} />
                            <datalist id="club-categories">
                                {categories.map((category) => <option key={category} value={category} />)}
                            </datalist>
                        </FormGroup>
                    </Col>
                </Row>
                {/* Cor da marca: define o card do parceiro na vitrine quando ele ainda não tem capa.
                    A prévia mostra esse card, para o gestor ver o efeito da cor na hora. */}
                <Row className="g-3 mt-1">
                    <Col md={7}>
                        <FormGroup>
                            <FormLabel>Cor da marca <HintTooltip text={HINTS.brandColor} /></FormLabel>
                            <ColorField label="cor da marca" value={formik.values.brandColor}
                                onChange={(color) => formik.setFieldValue('brandColor', color)} />
                        </FormGroup>
                    </Col>
                    <Col md={5}>
                        <FormLabel>Prévia na vitrine</FormLabel>
                        <div className="club-brand-preview"
                            style={{ '--club-brand': formik.values.brandColor || '#FB6A21' }}>
                            <span className="club-brand-preview__logo">{(formik.values.name || 'P').charAt(0).toUpperCase()}</span>
                            <strong>{formik.values.name || 'Nome do parceiro'}</strong>
                        </div>
                    </Col>
                </Row>
                <Row className="g-3 mt-1">
                    <Col md={12}>
                        <FormGroup>
                            <FormLabel>Descrição</FormLabel>
                            <TextArea name="description" maxLength={1000} counter minRows={2} value={formik.values.description}
                                onChange={formik.handleChange} className="form-control input-default" />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>Destaque <HintTooltip text={HINTS.featured} /></FormLabel>
                            <SegmentedButtons compact ariaLabel="Destaque" value={formik.values.featured}
                                onChange={(featured) => formik.setFieldValue('featured', featured)}
                                options={[{ value: false, label: 'Normal', icon: 'fa-regular fa-star' }, { value: true, label: 'Em destaque', icon: 'fa-solid fa-star' }]} />
                        </FormGroup>
                    </Col>
                </Row>
            </FormSection>

            <FormSection title="Contato e endereço" description="O cliente chega ao parceiro por aqui. Tudo é opcional. Informe o CEP para preencher o endereço sozinho." icon="fa-solid fa-location-dot">
                {/* Mesmo endereço da filial e do fornecedor, com consulta de CEP. */}
                <AddressRegistration formik={formik} />
                <Row className="g-3">
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>Site</FormLabel>
                            <InputText name="website" maxLength={255} placeholder="www.parceiro.com.br" value={formik.values.website} onChange={formik.handleChange} />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>WhatsApp</FormLabel>
                            <InputText name="whatsapp" maxLength={30} placeholder="(81) 99999-9999" value={formik.values.whatsapp} onChange={formik.handleChange} />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>Instagram</FormLabel>
                            <InputText name="instagram" maxLength={120} placeholder="@parceiro" value={formik.values.instagram} onChange={formik.handleChange} />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>Facebook</FormLabel>
                            <InputText name="facebook" maxLength={120} placeholder="parceiro" value={formik.values.facebook} onChange={formik.handleChange} />
                        </FormGroup>
                    </Col>
                </Row>
            </FormSection>

            <FormSection title="Arte" description="A logo e a capa são o que mais chama a atenção na vitrine. A imagem é enviada ao salvar." icon="fa-solid fa-palette">
                <div className="club-art">
                    <ArtSlot title="Logo" hint={HINTS.logo} kind="logo" maxSize={MAX_LOGO_SIZE}
                        savedPath={entityData?.logoPath} file={formik.values.logoFile} removed={formik.values.removeLogo}
                        onPick={(file) => { formik.setFieldValue('logoFile', file); formik.setFieldValue('removeLogo', false); }}
                        onRemove={() => { formik.setFieldValue('logoFile', null); formik.setFieldValue('removeLogo', true); }} />
                    <ArtSlot title="Capa" hint={HINTS.cover} kind="cover" maxSize={MAX_COVER_SIZE}
                        savedPath={entityData?.coverPath} file={formik.values.coverFile} removed={formik.values.removeCover}
                        onPick={(file) => { formik.setFieldValue('coverFile', file); formik.setFieldValue('removeCover', false); }}
                        onRemove={() => { formik.setFieldValue('coverFile', null); formik.setFieldValue('removeCover', true); }} />
                </div>
            </FormSection>

            <FormSection
                title="Ofertas"
                description="Cada oferta é uma vantagem para os assinantes. O cliente resgata e recebe um cupom individual, de uso único."
                icon="fa-solid fa-tags"
                actions={<Button size="sm" variant="outline-primary" onClick={addOffer}><i className="fa-solid fa-plus me-1"></i>Adicionar oferta</Button>}
            >
                {offers.length === 0 && (
                    <Alert variant="info" className="py-2 mb-0">
                        <i className="fa-solid fa-circle-info me-2"></i>Sem ofertas, o parceiro aparece na vitrine só com as informações de contato.
                    </Alert>
                )}

                {offers.map((offer, index) => {
                    const inactive = offer.status === 'INACTIVE';
                    return (
                        <div key={offer.id || `new-${index}`} className={`club-offer-edit ${inactive ? 'club-offer-edit--inactive' : ''}`}>
                            <div className="club-offer-edit__head">
                                <span className="club-offer-edit__title">
                                    <span className="club-offer-edit__index">{index + 1}</span>
                                    Oferta
                                    {offer.couponsGenerated > 0 && <small className="text-muted ms-2">{offer.couponsGenerated} cupons gerados</small>}
                                    {inactive && <small className="text-muted ms-2">(retirada da vitrine)</small>}
                                </span>
                                {inactive
                                    ? <Button size="sm" variant="outline-primary" onClick={() => updateOffer(index, { status: 'ACTIVE' })}>Reativar</Button>
                                    : <Button size="sm" variant="outline-danger" onClick={() => removeOffer(index)}>{offer.id ? 'Retirar' : 'Remover'}</Button>}
                            </div>
                            <Row className="g-3">
                                <Col md={12}>
                                    <FormLabel>Título <AsteriskRequired /></FormLabel>
                                    <InputText maxLength={120} placeholder="10% no rodízio" value={offer.title}
                                        onChange={(e) => updateOffer(index, { title: e.target.value })} error={offerError(index, 'title')} />
                                </Col>
                                <Col md={12}>
                                    <FormLabel>Regras <HintTooltip text={HINTS.rules} /></FormLabel>
                                    <div className="club-rules">
                                        {offer.rulesList.map((rule, ruleIndex) => (
                                            <div className="club-rules__item" key={ruleIndex}>
                                                <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
                                                {/* Uma regra recém-adicionada (vazia) já nasce com o foco. */}
                                                <input type="text" className="form-control input-default" maxLength={MAX_RULE_LENGTH}
                                                    autoFocus={rule === ''}
                                                    placeholder="Ex.: Válido de segunda a quinta" value={rule}
                                                    onChange={(e) => setRules(index, offer.rulesList.map((item, i) => (i === ruleIndex ? e.target.value : item)))}
                                                    onKeyDown={(e) => {
                                                        // Enter abre a próxima regra, como uma lista de anotações.
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            if (rule.trim() && offer.rulesList.length < MAX_RULES) setRules(index, [...offer.rulesList, '']);
                                                        }
                                                    }} />
                                                <button type="button" className="club-rules__remove" aria-label="Remover regra"
                                                    onClick={() => setRules(index, offer.rulesList.filter((_, i) => i !== ruleIndex))}>
                                                    <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                                                </button>
                                            </div>
                                        ))}
                                        {offer.rulesList.length < MAX_RULES && (
                                            <Button size="sm" variant="outline-primary" className="club-rules__add"
                                                onClick={() => setRules(index, [...offer.rulesList, ''])}>
                                                <i className="fa-solid fa-plus me-1"></i>Adicionar regra
                                            </Button>
                                        )}
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <FormLabel>Válida a partir de</FormLabel>
                                    <InputDate selected={offer.validFrom} onChange={(value) => updateOffer(index, { validFrom: value })} />
                                </Col>
                                <Col md={3}>
                                    <FormLabel>Válida até</FormLabel>
                                    <InputDate selected={offer.validUntil} onChange={(value) => updateOffer(index, { validUntil: value })} />
                                </Col>
                                <Col md={6}>
                                    <FormLabel>Planos com direito <HintTooltip text={HINTS.plans} /></FormLabel>
                                    <CustomSelect isMulti value={offer.plans} options={planOptions} placeholder="Todos os planos"
                                        onChange={(options) => updateOffer(index, { plans: options || [] })} />
                                </Col>
                                <Col xs={6} md={3}>
                                    <FormLabel>Cupons por cliente <HintTooltip text={HINTS.usageLimit} /></FormLabel>
                                    <input type="number" min="1" className="form-control input-default" placeholder="Sem limite" value={offer.usageLimit}
                                        onChange={(e) => updateOffer(index, { usageLimit: e.target.value })} />
                                </Col>
                                <Col xs={6} md={4}>
                                    <FormLabel>Contado</FormLabel>
                                    <SegmentedButtons compact ariaLabel="Período do limite" value={offer.usageLimitPeriod}
                                        onChange={(period) => updateOffer(index, { usageLimitPeriod: period })}
                                        options={[{ value: 'MONTH', label: 'Por mês' }, { value: 'TOTAL', label: 'No total' }]} />
                                </Col>
                                <Col xs={6} md={3}>
                                    <FormLabel>Validade do cupom (dias) <HintTooltip text={HINTS.validity} /></FormLabel>
                                    <input type="number" min="1" max="90" className="form-control input-default" value={offer.couponValidityDays}
                                        onChange={(e) => updateOffer(index, { couponValidityDays: e.target.value })} />
                                </Col>
                            </Row>
                        </div>
                    );
                })}
            </FormSection>

            <AccessSection entityData={entityData} company={company} onGenerated={onAccessGenerated} />
        </div>
    );
};

export default Form;
